/**
 * AssignReceivedMoneyModal — assign money received to client accounts and
 * their open raised invoices.
 *
 * Lists every raised (£ In) invoice that is awaiting money — sent for
 * payment, invoiced, part paid, overdue, or in debt collection — grouped by
 * client account. Quotations, drafts, scheduled/future rows, written-off,
 * fully-paid, credited, and received (supplier) invoices never appear
 * (server-side filter on GET /invoices/assignable).
 *
 * Selecting an invoice opens an inline payment form: amount (pre-filled
 * with the outstanding balance), date, Sage/bank reference, sender
 * reference, and the account the money was received into. Recording posts
 * to /invoices/:id/payments — the server recalculates amount_paid and
 * flips the status to Part Paid or Paid in Full automatically; a fully
 * satisfied invoice drops off this list on the refetch.
 *
 * Props:
 *   isOpen / onClose        — standard modal control
 *   focusInvoiceId (opt.)   — pre-expand this invoice's payment form (used
 *                             by the ledger "Assign £ Received" buttons)
 *   onAssigned (opt.)       — called after any successful payment so the
 *                             opener can refresh its own data
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Select, Spinner,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import api from '../api';

const STATUS_META = {
  sent_for_payment:         { label: 'Sent for Payment', color: 'blue' },
  invoiced_within_terms:    { label: 'Invoiced',         color: 'blue' },
  paid_in_part:             { label: 'Part Paid',        color: 'orange' },
  overdue:                  { label: 'Overdue',          color: 'red' },
  very_overdue:             { label: 'Very Overdue',     color: 'red' },
  send_for_debt_collection: { label: 'Debt Collection',  color: 'red' },
};

const fmtMoney = (v) => `£${parseFloat(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(String(d).split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const emptyPayForm = () => ({
  amount: '',
  payment_date: new Date().toISOString().split('T')[0],
  sage_reference: '',
  sender_reference: '',
  reference: '',
  to_bank_account_id: '',
});

export default function AssignReceivedMoneyModal({ isOpen, onClose, focusInvoiceId, onAssigned }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [collapsed, setCollapsed] = useState({});     // company name -> bool
  const [openInvoiceId, setOpenInvoiceId] = useState(null);
  const [payForm, setPayForm] = useState(emptyPayForm());
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/invoices/assignable');
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) { setInvoices([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetchInvoices();
    setCompanyFilter('');
    setCollapsed({});
    setOpenInvoiceId(null);
    setPayForm(emptyPayForm());
    api.get('/bank-accounts?is_own=true').then(r => setBankAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [isOpen, fetchInvoices]);

  // Pre-expand the focused invoice (ledger button flow) once data arrives.
  useEffect(() => {
    if (!isOpen || !focusInvoiceId || invoices.length === 0) return;
    const inv = invoices.find(i => String(i.id) === String(focusInvoiceId));
    if (inv) openPayment(inv);
  }, [isOpen, focusInvoiceId, invoices.length]); // eslint-disable-line

  const openPayment = (inv) => {
    setOpenInvoiceId(inv.id);
    setPayForm({ ...emptyPayForm(), amount: inv.outstanding > 0 ? String(inv.outstanding.toFixed ? inv.outstanding.toFixed(2) : inv.outstanding) : '' });
  };

  const handleRecord = async (inv) => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { toast({ title: 'Enter an amount', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      await api.post(`/invoices/${inv.id}/payments`, {
        amount: amt,
        payment_date: payForm.payment_date,
        sage_reference: payForm.sage_reference,
        sender_reference: payForm.sender_reference,
        reference: payForm.reference,
        to_bank_account_id: payForm.to_bank_account_id || null,
      });
      const satisfied = amt >= (parseFloat(inv.outstanding) || 0);
      toast({
        title: satisfied ? 'Payment assigned — invoice Paid in Full' : 'Payment assigned — invoice Part Paid',
        status: 'success', duration: 2500,
      });
      setOpenInvoiceId(null);
      setPayForm(emptyPayForm());
      await fetchInvoices();
      if (onAssigned) onAssigned();
    } catch (e) {
      toast({ title: 'Could not assign payment', description: e?.response?.data?.error || '', status: 'error', duration: 3000 });
    } finally { setSaving(false); }
  };

  // ── Group by client account ────────────────────────────────────────────
  const filtered = companyFilter
    ? invoices.filter(i => (i.company_name || '(no client)') === companyFilter)
    : invoices;
  const groups = {};
  filtered.forEach(inv => {
    const key = inv.company_name || '(no client)';
    (groups[key] = groups[key] || []).push(inv);
  });
  const companyNames = [...new Set(invoices.map(i => i.company_name || '(no client)'))];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent maxH="88vh">
        <ModalHeader fontSize="md">
          <Flex align="center" justify="space-between" pr={8}>
            <Box>
              <Text>💷 Assign Received Money</Text>
              <Text fontSize="11px" color="gray.500" fontWeight="400">
                Raised invoices awaiting payment, by client account. Fully satisfied invoices switch to Paid in Full automatically.
              </Text>
            </Box>
            <Button size="xs" leftIcon={<FiRefreshCw />} variant="outline" onClick={fetchInvoices} isLoading={loading}>
              Refresh
            </Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack mb={3} spacing={2}>
            <FormControl w="320px">
              <FormLabel fontSize="xs" mb={0.5}>Client account</FormLabel>
              <Select size="sm" value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
                <option value="">All clients ({invoices.length} open invoice{invoices.length === 1 ? '' : 's'})</option>
                {companyNames.map(n => <option key={n} value={n}>{n}</option>)}
              </Select>
            </FormControl>
          </HStack>

          {loading && invoices.length === 0 ? (
            <Flex justify="center" py={10}><Spinner /></Flex>
          ) : filtered.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
              No raised invoices are awaiting money right now.
            </Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {Object.entries(groups).map(([company, invs]) => {
                const totalOutstanding = invs.reduce((s, i) => s + (parseFloat(i.outstanding) || 0), 0);
                const isCollapsed = collapsed[company];
                return (
                  <Box key={company} border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                    <Flex align="center" gap={2} px={3} py={2} bg="green.50" cursor="pointer"
                      onClick={() => setCollapsed(p => ({ ...p, [company]: !p[company] }))}>
                      {isCollapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
                      <Text fontSize="13px" fontWeight="700" flex={1}>{company}</Text>
                      <Badge colorScheme="green" fontSize="9px">{invs.length} invoice{invs.length === 1 ? '' : 's'}</Badge>
                      <Text fontSize="12px" fontWeight="700" color="green.700">{fmtMoney(totalOutstanding)} outstanding</Text>
                    </Flex>
                    {!isCollapsed && invs.map(inv => {
                      const meta = STATUS_META[inv.status] || { label: inv.status, color: 'gray' };
                      const isOpenRow = openInvoiceId === inv.id;
                      return (
                        <Box key={inv.id} borderTop="1px" borderColor="gray.100" px={3} py={2}
                          bg={isOpenRow ? 'teal.50' : 'white'}>
                          <Flex align="center" gap={2} flexWrap="wrap">
                            <Text fontSize="12px" fontWeight="700" minW="90px">{inv.invoice_number || inv.invoice_ref || '—'}</Text>
                            <Badge colorScheme={meta.color} fontSize="8px">{meta.label}</Badge>
                            {!!inv.is_overdue && inv.status === 'paid_in_part' && (
                              <Badge colorScheme="red" fontSize="8px">OVERDUE</Badge>
                            )}
                            <Text fontSize="10px" color="gray.500" minW={0} flex={1} isTruncated>
                              {inv.project_name || ''}
                            </Text>
                            <Text fontSize="10px" color="gray.500">Due {fmtDate(inv.due_date)}</Text>
                            <Text fontSize="11px" color="gray.600">Gross {fmtMoney(inv.total_gross)}</Text>
                            <Text fontSize="11px" color="green.600">Paid {fmtMoney(inv.amount_paid)}</Text>
                            <Text fontSize="11px" fontWeight="700" color="red.600">Owed {fmtMoney(inv.outstanding)}</Text>
                            <Button size="xs" colorScheme="teal" variant={isOpenRow ? 'solid' : 'outline'}
                              onClick={() => isOpenRow ? setOpenInvoiceId(null) : openPayment(inv)}>
                              {isOpenRow ? 'Cancel' : 'Assign £ Received'}
                            </Button>
                          </Flex>
                          {isOpenRow && (
                            <Box mt={2} p={3} bg="white" border="1px" borderColor="teal.200" borderRadius="md">
                              <HStack spacing={2} flexWrap="wrap" align="flex-end">
                                <FormControl w="110px">
                                  <FormLabel fontSize="xs" mb={0.5}>Amount £</FormLabel>
                                  <Input size="xs" type="number" step="0.01" value={payForm.amount}
                                    onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} autoFocus />
                                </FormControl>
                                <FormControl w="140px">
                                  <FormLabel fontSize="xs" mb={0.5}>Date</FormLabel>
                                  <Input size="xs" type="date" value={payForm.payment_date}
                                    onChange={e => setPayForm(p => ({ ...p, payment_date: e.target.value }))} />
                                </FormControl>
                                <FormControl w="160px">
                                  <FormLabel fontSize="xs" mb={0.5}>Sage / Bank Ref</FormLabel>
                                  <Input size="xs" value={payForm.sage_reference}
                                    onChange={e => setPayForm(p => ({ ...p, sage_reference: e.target.value }))} />
                                </FormControl>
                                <FormControl w="160px">
                                  <FormLabel fontSize="xs" mb={0.5}>Sender Ref</FormLabel>
                                  <Input size="xs" value={payForm.sender_reference}
                                    onChange={e => setPayForm(p => ({ ...p, sender_reference: e.target.value }))} />
                                </FormControl>
                                <FormControl w="180px">
                                  <FormLabel fontSize="xs" mb={0.5}>Received Into</FormLabel>
                                  <Select size="xs" value={payForm.to_bank_account_id}
                                    onChange={e => setPayForm(p => ({ ...p, to_bank_account_id: e.target.value }))}>
                                    <option value="">—</option>
                                    {bankAccounts.map(b => (
                                      <option key={b.id} value={b.id}>
                                        {b.account_name || b.bank_name} {b.currency_code ? `(${b.currency_code})` : ''}
                                      </option>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Button size="xs" colorScheme="teal" onClick={() => handleRecord(inv)} isLoading={saving}>
                                  Record Payment
                                </Button>
                              </HStack>
                              <Text fontSize="9px" color="gray.400" mt={1}>
                                Assigning {payForm.amount ? fmtMoney(payForm.amount) : '£0.00'} of {fmtMoney(inv.outstanding)} outstanding
                                {parseFloat(payForm.amount || 0) >= parseFloat(inv.outstanding || 0) && parseFloat(payForm.amount || 0) > 0
                                  ? ' — this will mark the invoice Paid in Full.' : '.'}
                              </Text>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
